import { useEffect, useState, useCallback } from "react";
import { API } from "@/lib/api";

const CACHE_KEY = "sv_branding_v1";
const POLL_INTERVAL_MS = 60_000; // 60s — branding rarely changes, no need to hammer

/**
 * useBranding — single source of truth for the dynamic app logo.
 *
 * Returns `{logoUrl, brandingVersion, isCustom, reload}`:
 *   • `logoUrl`        Either the admin-uploaded logo (served via /api/files/...)
 *                      or `null` if the admin hasn't uploaded one yet (callers
 *                      then render the bundled SVG fallback at `/logo.svg`).
 *   • `brandingVersion` Monotonically increases every time the admin re-uploads
 *                       the logo. The PWA-update banner watches this value.
 *   • `isCustom`        True iff the admin has uploaded a custom logo.
 *   • `reload()`        Force refetch (used by the admin upload screen).
 *
 * Caches the last successful response in localStorage so the header logo
 * stays sharp even on a cold offline reload (no flash of fallback SVG).
 */
export function useBranding() {
  const [state, setState] = useState(() => {
    try {
      const raw = localStorage.getItem(CACHE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        return {
          logoPath: parsed.logoPath || null,
          brandingVersion: Number(parsed.brandingVersion) || 0,
        };
      }
    } catch { /* corrupt cache → ignore */ }
    return { logoPath: null, brandingVersion: 0 };
  });

  const fetchOnce = useCallback(async () => {
    try {
      const res = await fetch(`${API}/branding`, { cache: "no-store", credentials: "omit" });
      if (!res.ok) return;
      const data = await res.json();
      const next = {
        logoPath: data?.logo_path || null,
        brandingVersion: Number(data?.branding_version) || 0,
      };
      setState((prev) => {
        if (prev.logoPath === next.logoPath && prev.brandingVersion === next.brandingVersion) {
          return prev;
        }
        try { localStorage.setItem(CACHE_KEY, JSON.stringify(next)); } catch { /* quota */ }
        return next;
      });
    } catch { /* offline → keep cached value */ }
  }, []);

  useEffect(() => {
    fetchOnce();
    const id = setInterval(fetchOnce, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [fetchOnce]);

  const logoUrl = state.logoPath ? `${API}/files/${state.logoPath}?thumb=1` : null;
  return {
    logoUrl,
    brandingVersion: state.brandingVersion,
    isCustom: !!state.logoPath,
    reload: fetchOnce,
  };
}
